import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import MediaInput from "../../components/admin/MediaInput";

const BLOCK_TYPES = [
  { type: "paragraph", label: "Paragraph" },
  { type: "heading", label: "Heading (H2)" },
  { type: "image", label: "Image" },
  { type: "video", label: "Video (Vimeo)" },
  { type: "pullquote", label: "Pull quote" },
  { type: "internal-link", label: "Internal link card" },
  { type: "cta", label: "Mid-article CTA" },
  { type: "divider", label: "Divider" },
];

const newBlock = (type) => {
  const base = { type, text: "", level: 2, src: "", alt: "", caption: "", href: "", label: "" };
  if (type === "heading") base.level = 2;
  if (type === "cta") {
    base.text = "If this thinking resonates, we should work together.";
    base.label = "Start a project →";
    base.href = "/contact";
  }
  if (type === "internal-link") {
    base.label = "View case study";
    base.text = "Read the full breakdown";
    base.href = "/work";
  }
  return base;
};

/* ============================================================
   /admin/blog — list + new + edit
   ============================================================ */

export default function BlogAdmin() {
  const { slug } = useParams();
  // Two modes: list view (no slug, no /new) vs editor (slug or "new")
  return slug ? <BlogEditor key={slug} slug={slug} /> : <BlogList />;
}

function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = () => {
    setLoading(true);
    api.get("/blogs").then(({ data }) => {
      setBlogs(data || []);
      setLoading(false);
    });
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    await api.delete(`/blogs/${slug}`);
    refresh();
  };

  // Swap `order` values of two adjacent posts, then re-fetch the list.
  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blogs.length) return;
    const a = blogs[idx];
    const b = blogs[target];
    // Assign explicit order values (falling back to their list position + 1)
    const orderA = a.order != null ? a.order : idx + 1;
    const orderB = b.order != null ? b.order : target + 1;
    // Optimistic UI — swap in place while the PUTs fly
    const next = [...blogs];
    next[idx] = b;
    next[target] = a;
    setBlogs(next);
    try {
      await Promise.all([
        api.put(`/blogs/${a.slug}`, { ...a, order: orderB }),
        api.put(`/blogs/${b.slug}`, { ...b, order: orderA }),
      ]);
      refresh();
    } catch {
      refresh();
    }
  };

  return (
    <div data-testid="admin-blog-list" className="max-w-5xl space-y-8">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="overline text-[#C9A050]/70 mb-3">Blog</p>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Articles</h1>
          <p className="text-[#8A8A93] text-sm mt-2">{blogs.length} total</p>
        </div>
        <Link
          to="/admin/blog/new"
          data-testid="admin-blog-new"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all"
        >
          + New article
        </Link>
      </header>

      {loading && <p className="text-[#8A8A93]">Loading…</p>}

      <ul className="space-y-3">
        {blogs.map((b, idx) => (
          <li
            key={b.slug}
            data-testid={`admin-blog-row-${b.slug}`}
            className="border border-white/10 bg-white/[0.02] rounded-xl p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap hover:border-white/20 transition-colors"
          >
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  data-testid={`admin-blog-up-${b.slug}`}
                  className="w-7 h-7 flex items-center justify-center text-[#8A8A93] hover:text-[#C9A050] disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                >↑</button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === blogs.length - 1}
                  aria-label="Move down"
                  data-testid={`admin-blog-down-${b.slug}`}
                  className="w-7 h-7 flex items-center justify-center text-[#8A8A93] hover:text-[#C9A050] disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                >↓</button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-1.5">
                  <StatusPill status={b.status} />
                  {b.featured && <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A050]">Featured</span>}
                  <span className="overline text-[#8A8A93]">{b.kicker || (b.tags || []).join(" · ")}</span>
                </div>
                <p className="font-display text-xl md:text-2xl truncate">{b.title}</p>
                <p className="text-xs text-[#8A8A93] mt-1">/{b.slug} · {b.read_time_min || 1} min read</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/blog/${b.slug}`}
                target="_blank"
                rel="noreferrer"
                className="overline text-[#8A8A93] hover:text-white"
              >
                Preview
              </Link>
              <Link
                to={`/admin/blog/${b.slug}`}
                data-testid={`admin-blog-edit-${b.slug}`}
                className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-4 py-2 transition-all"
              >
                Edit
              </Link>
              <button
                onClick={() => remove(b.slug)}
                data-testid={`admin-blog-delete-${b.slug}`}
                className="overline text-[#FF8B8B]/70 hover:text-[#FF8B8B] transition-colors text-xs"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
        {!loading && blogs.length === 0 && <p className="text-[#8A8A93]">No articles yet.</p>}
      </ul>
    </div>
  );
}

function StatusPill({ status }) {
  const isPublished = status === "published";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.3em] px-2.5 py-1 ${isPublished ? "bg-[#C9A050]/20 text-[#C9A050]" : "bg-white/[0.05] text-[#8A8A93]"}`}>
      {isPublished ? "Published" : "Draft"}
    </span>
  );
}

/* ============================================================
   Editor — block-based
   ============================================================ */

function BlogEditor({ slug }) {
  const isNew = slug === "new";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (isNew) {
      setData({
        slug: "",
        title: "",
        subtitle: "",
        kicker: "",
        tags: [],
        cover_image: "",
        cover_video_url: "",
        cover_focal: "center",
        excerpt: "",
        blocks: [],
        author_name: "Artistry Perspectives",
        author_signature: "We build brands designed to last, not trend.",
        status: "draft",
        featured: false,
        published_at: "",
        read_time_min: null,
        meta_title: "",
        meta_description: "",
        og_image: "",
      });
    } else {
      api.get(`/blogs/${slug}`).then(({ data }) => setData(data));
    }
  }, [slug, isNew]);

  if (!data) return <p className="text-[#8A8A93]">Loading…</p>;

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setBlock = (i, patch) =>
    setData((d) => ({ ...d, blocks: d.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const addBlock = (type) => setData((d) => ({ ...d, blocks: [...(d.blocks || []), newBlock(type)] }));
  const removeBlock = (i) =>
    setData((d) => ({ ...d, blocks: d.blocks.filter((_, j) => j !== i) }));
  const moveBlock = (i, dir) =>
    setData((d) => {
      const next = [...d.blocks];
      const j = i + dir;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, blocks: next };
    });

  const save = async (overrideStatus) => {
    setBusy(true);
    setMsg("");
    try {
      const payload = { ...data };
      if (overrideStatus) payload.status = overrideStatus;
      // Tags: accept comma-separated string in UI; here `data.tags` is already array
      let saved;
      if (isNew) {
        saved = (await api.post("/blogs", payload)).data;
        navigate(`/admin/blog/${saved.slug}`, { replace: true });
      } else {
        saved = (await api.put(`/blogs/${slug}`, payload)).data;
        // If slug changed, navigate to new
        if (saved.slug !== slug) navigate(`/admin/blog/${saved.slug}`, { replace: true });
        else setData(saved);
      }
      setMsg(overrideStatus === "published" ? "Published." : "Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-blog-editor" className="max-w-4xl space-y-10">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <Link to="/admin/blog" className="overline text-[#8A8A93] hover:text-white">← All articles</Link>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-3">
            {isNew ? "New article" : "Edit article"}
          </h1>
          {!isNew && <p className="text-[#8A8A93] text-sm mt-1">/{data.slug}</p>}
        </div>
      </header>

      {/* Meta */}
      <Block title="Article basics">
        <Field label="Title" value={data.title} onChange={(v) => set({ title: v })} testid="bf-title" required />
        <Field label="Subtitle (optional, italic on hero)" value={data.subtitle} onChange={(v) => set({ subtitle: v })} testid="bf-subtitle" multi />
        <Field label="Kicker (e.g. 'Branding · Insight')" value={data.kicker} onChange={(v) => set({ kicker: v })} testid="bf-kicker" />
        <Field
          label="Tags (comma-separated)"
          value={(data.tags || []).join(", ")}
          onChange={(v) => set({ tags: v.split(",").map((t) => t.trim()).filter(Boolean) })}
          testid="bf-tags"
        />
        <Field label="Slug (URL — leave blank to auto-generate)" value={data.slug} onChange={(v) => set({ slug: v })} testid="bf-slug" />
        <Field label="Excerpt (shown on /blog index card)" value={data.excerpt} onChange={(v) => set({ excerpt: v })} testid="bf-excerpt" multi />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Published date</label>
            <input
              type="datetime-local"
              value={(data.published_at || "").slice(0, 16)}
              onChange={(e) => set({ published_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              data-testid="bf-published-at"
              className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white"
            />
            <p className="text-xs text-[#8A8A93] mt-1">Auto-set when you first Publish; edit here to override.</p>
          </div>
          <div>
            <label className="overline block mb-2">Read time (minutes)</label>
            <input
              type="number"
              min={1}
              placeholder="Auto if blank"
              value={data.read_time_min ?? ""}
              onChange={(e) => set({ read_time_min: e.target.value === "" ? null : Number(e.target.value) })}
              data-testid="bf-read-time"
              className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white"
            />
            <p className="text-xs text-[#8A8A93] mt-1">Shown as "N min read". Leave blank for auto-calculation from blocks.</p>
          </div>
        </div>
      </Block>

      {/* Cover */}
      <Block title="Cover">
        <div>
          <label className="overline block mb-2">Cover image</label>
          <MediaInput
            value={data.cover_image || ""}
            onChange={(v) => set({ cover_image: v })}
            accept="image"
            folder="blog"
            testid="bf-cover-image"
            placeholder="https://…"
          />
        </div>
        <Field label="Cover video URL (optional, plays under image)" value={data.cover_video_url} onChange={(v) => set({ cover_video_url: v })} testid="bf-cover-video" type="url" />
        <Field
          label="Cover focal point (CSS object-position, e.g. 'center top', '30% 60%')"
          value={data.cover_focal}
          onChange={(v) => set({ cover_focal: v })}
          testid="bf-cover-focal"
        />
        {data.cover_image && (
          <div>
            <p className="overline text-[#8A8A93] mb-2">Preview</p>
            <div className="aspect-[16/9] overflow-hidden bg-[#15151A]">
              <img src={data.cover_image} alt="" className="w-full h-full object-cover" style={{ objectPosition: data.cover_focal || "center" }} />
            </div>
          </div>
        )}
      </Block>

      {/* Content blocks */}
      <Block title="Content blocks">
        <p className="text-xs text-[#8A8A93] -mt-2 mb-4">
          Build the article body block-by-block. Drag to reorder is via ↑/↓ buttons.
          Inside paragraph text, use <code className="text-[#C9A050]">**bold**</code> for emphasis and <code className="text-[#C9A050]">[label](url)</code> for links.
        </p>
        <div className="space-y-4">
          {(data.blocks || []).map((b, i) => (
            <BlockEditor
              key={i}
              index={i}
              block={b}
              onChange={(patch) => setBlock(i, patch)}
              onMoveUp={() => moveBlock(i, -1)}
              onMoveDown={() => moveBlock(i, 1)}
              onRemove={() => removeBlock(i)}
            />
          ))}
          {(data.blocks || []).length === 0 && (
            <p className="text-[#8A8A93] text-sm italic">No blocks yet — add one below to start writing.</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-6">
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.type}
              type="button"
              onClick={() => addBlock(bt.type)}
              data-testid={`bf-add-${bt.type}`}
              className="overline border border-white/15 text-[#8A8A93] hover:border-[#C9A050] hover:text-[#C9A050] px-3 py-2 text-xs transition-all"
            >
              + {bt.label}
            </button>
          ))}
        </div>
      </Block>

      {/* Author */}
      <Block title="Author signature">
        <Field label="Author name" value={data.author_name} onChange={(v) => set({ author_name: v })} testid="bf-author-name" />
        <Field label="Signature line" value={data.author_signature} onChange={(v) => set({ author_signature: v })} testid="bf-author-sig" multi />
      </Block>

      {/* Settings */}
      <Block title="Publishing">
        <div className="flex items-center gap-8 flex-wrap">
          <Toggle label="Featured" checked={!!data.featured} onChange={(v) => set({ featured: v })} testid="bf-featured" />
          <span className="text-sm text-[#8A8A93]">
            Status: <span className={data.status === "published" ? "text-[#C9A050]" : "text-white"}>{data.status}</span>
          </span>
        </div>
      </Block>

      {/* SEO */}
      <Block title="SEO">
        <Field label="Meta title (defaults to article title)" value={data.meta_title} onChange={(v) => set({ meta_title: v })} testid="bf-meta-title" />
        <Field label="Meta description" value={data.meta_description} onChange={(v) => set({ meta_description: v })} testid="bf-meta-desc" multi />
        <Field label="OG image URL (defaults to cover)" value={data.og_image} onChange={(v) => set({ og_image: v })} testid="bf-og-image" type="url" />
      </Block>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-[#0A0A0C]/92 backdrop-blur-md py-4 border-t border-white/5 flex items-center gap-4 flex-wrap z-10">
        <button
          onClick={() => save("draft")}
          disabled={busy || !data.title}
          data-testid="bf-save-draft"
          className="overline border border-white/15 text-white hover:border-white/40 px-6 py-3 transition-all disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save draft"}
        </button>
        <button
          onClick={() => save("published")}
          disabled={busy || !data.title}
          data-testid="bf-publish"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-40"
        >
          {busy ? "Publishing…" : data.status === "published" ? "Update" : "Publish"}
        </button>
        {msg && <span className="text-[#C9A050] text-sm" data-testid="bf-save-msg">{msg}</span>}
      </div>
    </div>
  );
}

/* ---------- block editor row ---------- */

function BlockEditor({ index, block, onChange, onMoveUp, onMoveDown, onRemove }) {
  const t = block.type;
  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-xl p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <span className="overline text-[#C9A050]/80">{index + 1}. {t}</span>
        <div className="flex items-center gap-2">
          <IconBtn label="Move up" onClick={onMoveUp}>↑</IconBtn>
          <IconBtn label="Move down" onClick={onMoveDown}>↓</IconBtn>
          <button onClick={onRemove} className="text-[#FF8B8B]/70 hover:text-[#FF8B8B] text-xs uppercase tracking-widest" data-testid={`bf-block-remove-${index}`}>Remove</button>
        </div>
      </div>

      {(t === "paragraph" || t === "pullquote") && (
        <Field label={t === "paragraph" ? "Text" : "Quote"} value={block.text} onChange={(v) => onChange({ text: v })} testid={`bf-block-${index}-text`} multi />
      )}
      {t === "heading" && (
        <>
          <Field label="Heading text" value={block.text} onChange={(v) => onChange({ text: v })} testid={`bf-block-${index}-text`} />
          <div>
            <label className="overline block mb-2">Level</label>
            <select value={block.level || 2} onChange={(e) => onChange({ level: Number(e.target.value) })} className="bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050]">
              <option value={2}>H2 (large)</option>
              <option value={3}>H3 (smaller)</option>
            </select>
          </div>
        </>
      )}
      {t === "image" && (
        <>
          <div>
            <label className="overline block mb-2">Image</label>
            <MediaInput
              value={block.src || ""}
              onChange={(v) => onChange({ src: v })}
              accept="image"
              folder="blog"
              testid={`bf-block-${index}-src`}
              placeholder="https://…"
            />
          </div>
          <Field label="Alt text" value={block.alt} onChange={(v) => onChange({ alt: v })} testid={`bf-block-${index}-alt`} />
          <Field label="Caption (optional)" value={block.caption} onChange={(v) => onChange({ caption: v })} testid={`bf-block-${index}-caption`} />
        </>
      )}
      {t === "video" && (
        <>
          <Field label="Vimeo URL" value={block.src} onChange={(v) => onChange({ src: v })} testid={`bf-block-${index}-src`} type="url" />
          <Field label="Caption (optional)" value={block.caption} onChange={(v) => onChange({ caption: v })} testid={`bf-block-${index}-caption`} />
        </>
      )}
      {t === "internal-link" && (
        <>
          <Field label="Kicker (e.g. 'View case study')" value={block.label} onChange={(v) => onChange({ label: v })} testid={`bf-block-${index}-label`} />
          <Field label="Link text" value={block.text} onChange={(v) => onChange({ text: v })} testid={`bf-block-${index}-text`} />
          <Field label="URL (e.g. /work/snow-village or /blog/zomato)" value={block.href} onChange={(v) => onChange({ href: v })} testid={`bf-block-${index}-href`} />
        </>
      )}
      {t === "cta" && (
        <>
          <Field label="Lead-in text (italic)" value={block.text} onChange={(v) => onChange({ text: v })} testid={`bf-block-${index}-text`} multi />
          <Field label="Button label" value={block.label} onChange={(v) => onChange({ label: v })} testid={`bf-block-${index}-label`} />
          <Field label="Button URL" value={block.href} onChange={(v) => onChange({ href: v })} testid={`bf-block-${index}-href`} />
        </>
      )}
      {t === "divider" && <p className="text-xs text-[#8A8A93]">A subtle gold gradient hairline. No content needed.</p>}
    </div>
  );
}

function IconBtn({ children, onClick, label }) {
  return (
    <button onClick={onClick} title={label} className="text-[#8A8A93] hover:text-[#C9A050] w-7 h-7 rounded border border-white/10 flex items-center justify-center text-sm transition-colors">
      {children}
    </button>
  );
}

function Block({ title, children }) {
  return (
    <section className="border border-white/10 bg-white/[0.02] rounded-xl p-6 md:p-8 space-y-5">
      <h2 className="font-display text-2xl">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, testid, multi, type = "text", required }) {
  if (multi) {
    return (
      <div>
        <label className="overline block mb-2">{label}{required && <span className="text-[#FF8B8B] ml-1">*</span>}</label>
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-transparent border border-white/10 focus:border-[#C9A050]/60 rounded px-3 py-2 outline-none transition-colors resize-y min-h-[64px] text-sm"
        />
      </div>
    );
  }
  return (
    <div>
      <label className="overline block mb-2">{label}{required && <span className="text-[#FF8B8B] ml-1">*</span>}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A050] py-2 outline-none transition-colors text-sm"
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="inline-flex items-center gap-3 select-none">
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${checked ? "bg-[#C9A050]" : "bg-white/15"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
      <span className="text-sm">{label}</span>
    </div>
  );
}
